/**
 * v1.3.6 — Pagina di selezione contesto (hard-gate).
 * Mostrata per utenti multi-scope che non hanno ancora scelto azienda + UL.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Building2, MapPin, ArrowRight, LogOut } from 'lucide-react';

export default function SelectContextPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentAziendaId, currentPdvId, setCurrentAziendaId, setCurrentPdvId } = useCurrentPdv();
  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/aziende').then(r => {
      setAziende(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!currentAziendaId) { setPdvList([]); return; }
    api.get(`/pdv?azienda_id=${currentAziendaId}`)
      .then(r => setPdvList(r.data || []))
      .catch(() => setPdvList([]));
  }, [currentAziendaId]);

  // Se contesto già completo, vai a dashboard
  useEffect(() => {
    if (currentAziendaId && currentPdvId) navigate('/dashboard');
  }, [currentAziendaId, currentPdvId, navigate]);

  const handleContinue = () => {
    if (!currentAziendaId || !currentPdvId) return;
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-xl shadow-xl" data-testid="select-context-card">
        <CardHeader className="text-center border-b">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <Building2 className="w-6 h-6 text-emerald-700" />
          </div>
          <CardTitle className="text-xl">Seleziona contesto operativo</CardTitle>
          <p className="text-sm text-slate-500 mt-2">
            Ciao <strong>{user?.first_name || user?.email}</strong>, scegli l&apos;azienda e l&apos;unità locale su cui vuoi
            operare. Tutte le pagine, documenti e notifiche faranno riferimento a questa scelta.
          </p>
        </CardHeader>
        <CardContent className="space-y-5 pt-6">
          <div>
            <Label className="text-sm flex items-center gap-1 mb-1.5">
              <Building2 className="w-3.5 h-3.5" /> Azienda <span className="text-red-600">*</span>
            </Label>
            <Select value={currentAziendaId || ''} onValueChange={setCurrentAziendaId} disabled={loading}>
              <SelectTrigger data-testid="ctx-azienda-select">
                <SelectValue placeholder={loading ? 'Caricamento...' : "Seleziona azienda..."} />
              </SelectTrigger>
              <SelectContent>
                {aziende.map(a => <SelectItem key={a.id} value={a.id}>{a.denominazione}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm flex items-center gap-1 mb-1.5">
              <MapPin className="w-3.5 h-3.5" /> Unità Locale <span className="text-red-600">*</span>
            </Label>
            <Select value={currentPdvId || ''} onValueChange={setCurrentPdvId} disabled={!currentAziendaId || pdvList.length === 0}>
              <SelectTrigger data-testid="ctx-pdv-select">
                <SelectValue placeholder={!currentAziendaId ? "Seleziona prima un'azienda..." : pdvList.length === 0 ? "Nessuna UL per questa azienda" : "Seleziona unità locale..."} />
              </SelectTrigger>
              <SelectContent>
                {pdvList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.city ? ` — ${p.city}` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 pt-3">
            <Button variant="outline" onClick={() => { logout?.(); navigate('/login'); }} className="flex-shrink-0" data-testid="ctx-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={!currentAziendaId || !currentPdvId}
              onClick={handleContinue}
              data-testid="ctx-continue-btn"
            >
              Continua <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 text-center">
            Puoi cambiare contesto in qualsiasi momento dal pulsante in alto a sinistra nella sidebar.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
